/**
 * Statuses and legal transitions.
 *
 * State is tracked on three documents rather than one, because a PO can be
 * partially received while one invoice is matched and another has not arrived.
 * A single status column cannot express that (docs/03).
 *
 * The transition maps here drive the UI (which buttons to show). The database
 * function `app.transition_request` is the authority — it re-checks every
 * transition, permission and guard. This map existing does not make it safe to
 * trust the client.
 */

// ---------------------------------------------------------------------------
// Purchase request — approval lifecycle
// ---------------------------------------------------------------------------

export const REQUEST_STATUS = {
  DRAFT: 'draft',
  PENDING_HOD: 'pending_hod',
  PENDING_HEAD_TEACHER: 'pending_head_teacher',
  PENDING_ESCALATED_APPROVAL: 'pending_escalated_approval',
  RETURNED_FOR_AMENDMENT: 'returned_for_amendment',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  CANCELLED: 'cancelled',
  PO_ISSUED: 'po_issued',
  CLOSED: 'closed',
} as const;

export type RequestStatus = (typeof REQUEST_STATUS)[keyof typeof REQUEST_STATUS];

export const REQUEST_ACTION = {
  SUBMIT: 'submit',
  RECOMMEND: 'recommend',
  APPROVE: 'approve',
  REJECT: 'reject',
  RETURN: 'return',
  CANCEL: 'cancel',
  RESUBMIT: 'resubmit',
} as const;

export type RequestAction = (typeof REQUEST_ACTION)[keyof typeof REQUEST_ACTION];

/** Terminal states. Nothing leaves these. */
export const TERMINAL_REQUEST_STATUSES: RequestStatus[] = [
  REQUEST_STATUS.REJECTED,
  REQUEST_STATUS.CANCELLED,
  REQUEST_STATUS.CLOSED,
];

/** Statuses awaiting somebody's approval decision. */
export const PENDING_APPROVAL_STATUSES: RequestStatus[] = [
  REQUEST_STATUS.PENDING_HOD,
  REQUEST_STATUS.PENDING_HEAD_TEACHER,
  REQUEST_STATUS.PENDING_ESCALATED_APPROVAL,
];

/**
 * Which actions are structurally possible from each status. The destination
 * depends on approval rules and amount, so it is resolved server-side — this
 * map only answers "could this button ever apply here?".
 */
export const REQUEST_ACTIONS_BY_STATUS: Record<RequestStatus, RequestAction[]> = {
  [REQUEST_STATUS.DRAFT]: [REQUEST_ACTION.SUBMIT, REQUEST_ACTION.CANCEL],
  [REQUEST_STATUS.PENDING_HOD]: [
    REQUEST_ACTION.RECOMMEND,
    REQUEST_ACTION.REJECT,
    REQUEST_ACTION.RETURN,
    REQUEST_ACTION.CANCEL,
  ],
  [REQUEST_STATUS.PENDING_HEAD_TEACHER]: [
    REQUEST_ACTION.APPROVE,
    REQUEST_ACTION.REJECT,
    REQUEST_ACTION.RETURN,
    REQUEST_ACTION.CANCEL,
  ],
  [REQUEST_STATUS.PENDING_ESCALATED_APPROVAL]: [
    REQUEST_ACTION.APPROVE,
    REQUEST_ACTION.REJECT,
    REQUEST_ACTION.RETURN,
    REQUEST_ACTION.CANCEL,
  ],
  [REQUEST_STATUS.RETURNED_FOR_AMENDMENT]: [REQUEST_ACTION.RESUBMIT, REQUEST_ACTION.CANCEL],
  // Cancelling here releases the budget commitment (docs/03).
  [REQUEST_STATUS.APPROVED]: [REQUEST_ACTION.CANCEL],
  [REQUEST_STATUS.REJECTED]: [],
  [REQUEST_STATUS.CANCELLED]: [],
  [REQUEST_STATUS.PO_ISSUED]: [],
  [REQUEST_STATUS.CLOSED]: [],
};

/** Actions that cannot proceed without a comment. Enforced in the database. */
export const ACTIONS_REQUIRING_COMMENT: RequestAction[] = [
  REQUEST_ACTION.REJECT,
  REQUEST_ACTION.RETURN,
];

// ---------------------------------------------------------------------------
// Purchase order — fulfilment lifecycle
// ---------------------------------------------------------------------------

export const PO_STATUS = {
  DRAFT: 'draft',
  ISSUED: 'issued',
  ACKNOWLEDGED: 'acknowledged',
  PARTIALLY_RECEIVED: 'partially_received',
  RECEIVED: 'received',
  INVOICED: 'invoiced',
  CLOSED: 'closed',
  CANCELLED: 'cancelled',
} as const;

export type PoStatus = (typeof PO_STATUS)[keyof typeof PO_STATUS];

// ---------------------------------------------------------------------------
// Invoice — financial lifecycle
// ---------------------------------------------------------------------------

export const INVOICE_STATUS = {
  UPLOADED: 'uploaded',
  MATCHING: 'matching',
  MATCHED: 'matched',
  DISCREPANCY: 'discrepancy',
  READY_FOR_XERO: 'ready_for_xero',
  EXPORTED: 'exported',
  PAID: 'paid',
  DISPUTED: 'disputed',
  CANCELLED: 'cancelled',
} as const;

export type InvoiceStatus = (typeof INVOICE_STATUS)[keyof typeof INVOICE_STATUS];

// ---------------------------------------------------------------------------
// Goods receipt
// ---------------------------------------------------------------------------

export const RECEIPT_STATUS = {
  RECEIVED_IN_FULL: 'received_in_full',
  PARTIALLY_RECEIVED: 'partially_received',
  REJECTED: 'rejected',
  OUTSTANDING: 'outstanding',
} as const;

export type ReceiptStatus = (typeof RECEIPT_STATUS)[keyof typeof RECEIPT_STATUS];

// ---------------------------------------------------------------------------
// Supporting enums
// ---------------------------------------------------------------------------

export const PRIORITY = {
  LOW: 'low',
  NORMAL: 'normal',
  HIGH: 'high',
  URGENT: 'urgent',
} as const;

export type Priority = (typeof PRIORITY)[keyof typeof PRIORITY];

export const SUPPLIER_STATUS = {
  PENDING_APPROVAL: 'pending_approval',
  APPROVED: 'approved',
  SUSPENDED: 'suspended',
  ARCHIVED: 'archived',
} as const;

export type SupplierStatus = (typeof SUPPLIER_STATUS)[keyof typeof SUPPLIER_STATUS];

export const USER_STATUS = {
  INVITED: 'invited',
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  LEFT: 'left',
} as const;

export type UserStatus = (typeof USER_STATUS)[keyof typeof USER_STATUS];

export const DISCREPANCY_TYPE = {
  PRICE: 'price',
  QUANTITY: 'quantity',
  VAT: 'vat',
  TOTAL: 'total',
  MISSING_RECEIPT: 'missing_receipt',
  UNKNOWN_LINE: 'unknown_line',
  DUPLICATE_INVOICE: 'duplicate_invoice',
  SUPPLIER_MISMATCH: 'supplier_mismatch',
  CURRENCY_MISMATCH: 'currency_mismatch',
  LATE_INVOICE: 'late_invoice',
} as const;

export type DiscrepancyType = (typeof DISCREPANCY_TYPE)[keyof typeof DISCREPANCY_TYPE];

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

export type StatusTone = 'neutral' | 'info' | 'pending' | 'success' | 'warning' | 'danger';

export const REQUEST_STATUS_META: Record<RequestStatus, { label: string; tone: StatusTone }> = {
  [REQUEST_STATUS.DRAFT]: { label: 'Draft', tone: 'neutral' },
  [REQUEST_STATUS.PENDING_HOD]: { label: 'Awaiting head of department', tone: 'pending' },
  [REQUEST_STATUS.PENDING_HEAD_TEACHER]: { label: 'Awaiting head teacher', tone: 'pending' },
  [REQUEST_STATUS.PENDING_ESCALATED_APPROVAL]: { label: 'Awaiting senior approval', tone: 'pending' },
  [REQUEST_STATUS.RETURNED_FOR_AMENDMENT]: { label: 'Returned for amendment', tone: 'warning' },
  [REQUEST_STATUS.APPROVED]: { label: 'Approved', tone: 'success' },
  [REQUEST_STATUS.REJECTED]: { label: 'Rejected', tone: 'danger' },
  [REQUEST_STATUS.CANCELLED]: { label: 'Cancelled', tone: 'neutral' },
  [REQUEST_STATUS.PO_ISSUED]: { label: 'Order placed', tone: 'info' },
  [REQUEST_STATUS.CLOSED]: { label: 'Closed', tone: 'success' },
};

export const INVOICE_STATUS_META: Record<InvoiceStatus, { label: string; tone: StatusTone }> = {
  [INVOICE_STATUS.UPLOADED]: { label: 'Uploaded', tone: 'neutral' },
  [INVOICE_STATUS.MATCHING]: { label: 'Matching', tone: 'pending' },
  [INVOICE_STATUS.MATCHED]: { label: 'Matched', tone: 'success' },
  [INVOICE_STATUS.DISCREPANCY]: { label: 'Discrepancy', tone: 'danger' },
  [INVOICE_STATUS.READY_FOR_XERO]: { label: 'Ready for Xero', tone: 'success' },
  [INVOICE_STATUS.EXPORTED]: { label: 'Exported', tone: 'info' },
  [INVOICE_STATUS.PAID]: { label: 'Paid', tone: 'success' },
  [INVOICE_STATUS.DISPUTED]: { label: 'Disputed', tone: 'warning' },
  [INVOICE_STATUS.CANCELLED]: { label: 'Cancelled', tone: 'neutral' },
};

export const PO_STATUS_META: Record<PoStatus, { label: string; tone: StatusTone }> = {
  [PO_STATUS.DRAFT]: { label: 'Draft', tone: 'neutral' },
  [PO_STATUS.ISSUED]: { label: 'Issued', tone: 'info' },
  [PO_STATUS.ACKNOWLEDGED]: { label: 'Acknowledged', tone: 'info' },
  [PO_STATUS.PARTIALLY_RECEIVED]: { label: 'Partially received', tone: 'warning' },
  [PO_STATUS.RECEIVED]: { label: 'Received', tone: 'success' },
  [PO_STATUS.INVOICED]: { label: 'Invoiced', tone: 'info' },
  [PO_STATUS.CLOSED]: { label: 'Closed', tone: 'success' },
  [PO_STATUS.CANCELLED]: { label: 'Cancelled', tone: 'neutral' },
};
